import React from "react";
import MKTypography from "components/MKTypography";
import MKBox from "components/MKBox";

// Enhanced markdown parser for MKPro components
export const parseMarkdownToMKPro = (markdown) => {
  if (!markdown) return [];

  const lines = markdown.split("\n");
  const elements = [];
  let listItems = [];
  let inCodeBlock = false;
  let codeBlockContent = [];
  let key = 0;

  const flushListItems = () => {
    if (listItems.length > 0) {
      elements.push(
        <MKBox key={`list-${key++}`} component="ul" sx={{ pl: 2, mb: 2 }}>
          {listItems.map((item, index) => (
            <MKBox key={index} component="li" sx={{ mb: 0.5 }}>
              <MKTypography variant="body2">{parseInlineMarkdown(item)}</MKTypography>
            </MKBox>
          ))}
        </MKBox>
      );
      listItems = [];
    }
  };

  const flushCodeBlock = () => {
    if (codeBlockContent.length > 0) {
      elements.push(
        <MKBox
          key={`code-${key++}`}
          sx={{
            backgroundColor: "#f5f5f5",
            p: 2,
            borderRadius: 1,
            fontFamily: "monospace",
            mb: 2,
            overflow: "auto",
          }}
        >
          <MKTypography variant="body2" component="pre">
            {codeBlockContent.join("\n")}
          </MKTypography>
        </MKBox>
      );
      codeBlockContent = [];
    }
  };

  lines.forEach((line) => {
    const trimmedLine = line.trim();

    // Handle code blocks
    if (trimmedLine.startsWith("```")) {
      if (inCodeBlock) {
        flushCodeBlock();
        inCodeBlock = false;
      } else {
        flushListItems();
        inCodeBlock = true;
      }
      return;
    }

    if (inCodeBlock) {
      codeBlockContent.push(line);
      return;
    }

    // Handle headers
    if (trimmedLine.startsWith("# ")) {
      flushListItems();
      elements.push(
        <MKTypography key={key++} variant="h4" sx={{ mb: 2, mt: 2 }}>
          {parseInlineMarkdown(trimmedLine.substring(2))}
        </MKTypography>
      );
    } else if (trimmedLine.startsWith("## ")) {
      flushListItems();
      elements.push(
        <MKTypography key={key++} variant="h5" sx={{ mb: 1.5, mt: 2 }}>
          {parseInlineMarkdown(trimmedLine.substring(3))}
        </MKTypography>
      );
    } else if (trimmedLine.startsWith("### ")) {
      flushListItems();
      elements.push(
        <MKTypography key={key++} variant="h6" sx={{ mb: 1, mt: 1.5 }}>
          {parseInlineMarkdown(trimmedLine.substring(4))}
        </MKTypography>
      );
    }
    // Handle unordered lists
    else if (trimmedLine.startsWith("- ") || trimmedLine.startsWith("* ")) {
      listItems.push(trimmedLine.substring(2));
    }
    // Handle ordered lists
    else if (/^\d+\.\s/.test(trimmedLine)) {
      listItems.push(trimmedLine.replace(/^\d+\.\s/, ""));
    }
    // Handle blockquotes
    else if (trimmedLine.startsWith("> ")) {
      flushListItems();
      elements.push(
        <MKBox
          key={key++}
          sx={{
            borderLeft: "4px solid #ddd",
            pl: 2,
            py: 1,
            mb: 2,
            backgroundColor: "#f9f9f9",
          }}
        >
          <MKTypography variant="body2" sx={{ fontStyle: "italic" }}>
            {parseInlineMarkdown(trimmedLine.substring(2))}
          </MKTypography>
        </MKBox>
      );
    }
    // Handle horizontal rules
    else if (trimmedLine === "---" || trimmedLine === "***") {
      flushListItems();
      elements.push(
        <MKBox
          key={key++}
          sx={{
            height: "1px",
            backgroundColor: "#ddd",
            my: 3,
            width: "100%",
          }}
        />
      );
    }
    // Handle empty lines
    else if (trimmedLine === "") {
      flushListItems();
      // Add some spacing for empty lines between paragraphs
      if (elements.length > 0) {
        elements.push(<MKBox key={key++} sx={{ mb: 1 }} />);
      }
    }
    // Handle regular paragraphs
    else {
      flushListItems();
      elements.push(
        <MKTypography key={key++} variant="body2" sx={{ mb: 1.5 }}>
          {parseInlineMarkdown(trimmedLine)}
        </MKTypography>
      );
    }
  });

  // Flush any remaining items
  flushListItems();
  flushCodeBlock();

  return elements;
};

// Parse inline markdown (bold, italic, code, links)
const parseInlineMarkdown = (text) => {
  const elements = [];
  let current = text;
  let key = 0;

  while (current.length > 0) {
    // Handle inline code
    const codeMatch = current.match(/^`([^`]+)`/);
    if (codeMatch) {
      elements.push(
        <MKBox
          key={key++}
          component="span"
          sx={{
            backgroundColor: "#f0f0f0",
            px: 0.5,
            borderRadius: 0.5,
            fontFamily: "monospace",
            fontSize: "0.9em",
          }}
        >
          {codeMatch[1]}
        </MKBox>
      );
      current = current.substring(codeMatch[0].length);
      continue;
    }

    // Handle bold text
    const boldMatch = current.match(/^\*\*([^*]+)\*\*/);
    if (boldMatch) {
      elements.push(
        <MKBox key={key++} component="strong">
          {boldMatch[1]}
        </MKBox>
      );
      current = current.substring(boldMatch[0].length);
      continue;
    }

    // Handle italic text
    const italicMatch = current.match(/^\*([^*]+)\*/);
    if (italicMatch) {
      elements.push(
        <MKBox key={key++} component="em">
          {italicMatch[1]}
        </MKBox>
      );
      current = current.substring(italicMatch[0].length);
      continue;
    }

    // Handle links
    const linkMatch = current.match(/^\[([^\]]+)\]\(([^)]+)\)/);
    if (linkMatch) {
      elements.push(
        <MKBox
          key={key++}
          component="a"
          href={linkMatch[2]}
          sx={{
            color: "primary.main",
            textDecoration: "underline",
            "&:hover": {
              textDecoration: "none",
            },
          }}
        >
          {linkMatch[1]}
        </MKBox>
      );
      current = current.substring(linkMatch[0].length);
      continue;
    }

    // Handle regular text
    const nextSpecial = current.search(/[`*[]/);
    if (nextSpecial === -1) {
      elements.push(current);
      break;
    } else {
      elements.push(current.substring(0, nextSpecial));
      current = current.substring(nextSpecial);
    }
  }

  return elements.length === 1 && typeof elements[0] === "string" ? elements[0] : elements;
};
